import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import SuburbField from "@/components/SuburbField";
import { SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", suburb: "", service_type: "", property_size: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [suburbOk, setSuburbOk] = useState(false);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.suburb || !form.service_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!suburbOk) {
      toast.error("Sorry, we don't currently service that suburb. Call us on +61 410 718 813 to check.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        preferred_date: date ? format(date, "PPP") : "",
      });
      setDone(true);
      toast.success("Quote request sent! Check your inbox for confirmation.");
      setForm(initial);
      setSuburbOk(false);
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please call us on +61 410 718 813.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="bg-white rounded-3xl border border-slate-200 p-10 text-center">
        <div className="w-16 h-16 rounded-full bg-[#E8F1EB] flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={32} className="text-[#0D9488]" />
        </div>
        <h3 className="font-display font-medium text-3xl text-[#1C2A39] mb-3">Request received!</h3>
        <p className="text-[#475569] max-w-md mx-auto">Thanks — we've emailed you a confirmation and our Adelaide team will be in touch shortly with your free quote.</p>
        <button data-testid="quote-another-btn" onClick={() => setDone(false)} className="btn-ghost mt-8">Send another request</button>
      </div>
    );
  }

  return (
    <form data-testid="quote-form" onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-9">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full name *">
          <Input data-testid="quote-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Smith" className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" />
        </Field>
        <Field label="Email *">
          <Input data-testid="quote-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@email.com" className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" />
        </Field>
        <Field label="Phone *">
          <Input data-testid="quote-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="04XX XXX XXX" className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" />
        </Field>
        <Field label="Suburb *">
          <SuburbField value={form.suburb} onChange={(v, ok) => { update("suburb", v); setSuburbOk(ok); }} />
        </Field>
        <Field label="Service *">
          <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
            <SelectTrigger data-testid="quote-service" className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200"><SelectValue placeholder="Choose a service" /></SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred date">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="quote-date" className="w-full h-12 rounded-xl border border-slate-200 bg-[#FAFAF9] px-3 flex items-center gap-2 text-sm text-left text-[#1C2A39]">
                <CalendarIcon size={16} className="text-[#0D9488]" />
                {date ? format(date, "PPP") : <span className="text-slate-400">Pick a date</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
            </PopoverContent>
          </Popover>
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Property size / details">
          <Input data-testid="quote-size" value={form.property_size} onChange={(e) => update("property_size", e.target.value)} placeholder="e.g. 3 bed, 2 bath house" className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200" />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Anything else?">
          <Textarea data-testid="quote-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your cleaning needs..." rows={4} className="bg-[#FAFAF9] rounded-xl border-slate-200 resize-none" />
        </Field>
      </div>

      <button data-testid="quote-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center mt-7 !py-4 disabled:opacity-70">
        {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : "Request My Free Quote"}
      </button>
      <p className="text-xs text-[#475569] text-center mt-4">No obligation · Fast response · Police-checked & satisfaction guaranteed</p>
    </form>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-[#1C2A39] text-sm font-medium">{label}</Label>
    {children}
  </div>
);
